import { AlertTriangle } from "lucide-react"

import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/tooltip"
import { cn } from "@/domains/library"

/**
 * Reference to an as-yet-un-shipped indexer proposal. Kept as a typed enum so
 * the tooltip copy and docs link stay in lockstep — when a proposal lands
 * simply remove the entry and the compiler will point to every call site
 * that needs cleaning up.
 */
export type IndexerProposal = "P1" | "P2" | "P3" | "P4" | "P5" | "P6"

interface ProposalInfo {
  title: string
  description: string
}

const PROPOSAL_COPY: Record<IndexerProposal, ProposalInfo> = {
  P1: {
    title: "Backfill `Pool.liquidityProviderCount`",
    description:
      "Real active-LP counts arrive with indexer Proposal 1. This figure is zero until the backfill lands.",
  },
  P2: {
    title: "`Pool.feeGrowthGlobal0/1` + per-interval fees",
    description:
      "Fee-growth and accurate per-interval fee figures arrive with indexer Proposal 2.",
  },
  P3: {
    title: "Canonical id formats in README",
    description: "Documentation-only indexer proposal; not user-facing.",
  },
  P4: {
    title: "`Position.nfpmTokenIds` backfill",
    description:
      "The on-chain NFPM token id for this position will appear here once indexer Proposal 4 ships. Manage / Increase / Decrease / Collect / Burn actions remain disabled until then.",
  },
  P5: {
    title: "`Pool.positions` nested relation",
    description:
      "The top-positions-in-pool table relies on the nested Pool.positions relation shipping with indexer Proposal 5.",
  },
  P6: {
    title: "`IndexerStatus` freshness entity",
    description:
      "Indexer lag / freshness telemetry ships with Proposal 6. The health banner stays hidden until then.",
  },
}

/**
 * Yellow-triangle badge used across the Manual CL surface to mark UI that is
 * wired against an indexer field which has not yet shipped. Hovering reveals
 * the proposal number and a short description so engineers/QA know exactly
 * where the backend gap lives.
 *
 * Keep this component dumb — it takes a single `proposal` prop and renders
 * the triangle + tooltip. The decision to show it lives in the parent
 * composite (`store.nfpmTokenIdPending`, `poolDayData.length === 0`, etc.).
 */
export function IndexerPendingIndicator({
  proposal,
  className,
  ariaLabel,
  size = "sm",
}: {
  proposal: IndexerProposal
  className?: string
  ariaLabel?: string
  size?: "xs" | "sm" | "md"
}) {
  const info = PROPOSAL_COPY[proposal]
  const iconSize = size === "xs" ? "h-3 w-3" : size === "md" ? "h-5 w-5" : "h-4 w-4"
  return (
    <TooltipProvider delayDuration={150}>
      <Tooltip>
        <TooltipTrigger asChild>
          <span
            className={cn("inline-flex items-center", className)}
            aria-label={ariaLabel ?? `Pending indexer ${proposal}: ${info.title}`}
            data-testid={`indexer-pending-indicator-${proposal.toLowerCase()}`}
          >
            <AlertTriangle
              className={cn(iconSize, "text-amber-400")}
              strokeWidth={2}
              aria-hidden
            />
          </span>
        </TooltipTrigger>
        <TooltipContent className="max-w-xs text-xs">
          <div className="text-t100 font-medium">
            Pending indexer {proposal} — {info.title}
          </div>
          <div className="text-t200 mt-1 leading-snug">{info.description}</div>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  )
}
